// @flow
import * as React from "react";
import styled, { css, ThemeProvider, ThemeConsumer } from "styled-components";
import { connect } from "react-redux";
import memoize from "lodash/memoize";
import moment from "moment";
import Media from "react-media";
import Money from "../Money";
import Gettext from "../common/Gettext";
import Refund from "../common/Refund";
import ClickOutside from "../common/ClickOutside";
import Close from "../Icons/Close";
import Layers from "../Chart/Layers";
import Chart from "../Chart/ClickhouseChart";
import Surface from "../Chart/Surface";
import Line from "../Chart/Line";
import { VerticalTicks, HorizontalTicks } from "../Chart/NextGenTicks";
import Deal, { LeftIndicatorView } from "../Chart/Deal";
import Api from "../Tournaments/Api";
import { tournamentCurrency } from "../Icons/Kuchka";
import Spinner from "../Spinners/SimpleSpinner";
import { apiCall } from "../common/fetch";
import * as Helpers from "../Chart/NextGenChartMath";

import type {
  Deal as Operation,
  Profile,
  Stock as Asset
} from "../../redux/CommonTypes";
import { UpIcon, DownIcon } from "../Icons/Bet";
import { TradeThemeContext } from "../ThemeProvider";
// import ForexIcon from "../Icons/Forex";
// import { InstrumentsIcon } from "../Toolbar/Icons";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  max-height: 100%;
  transform: translate3d(0, 0, 0);
  background: rgba(0, 0, 0, 0.54);
  z-index: 500;
  overflow: auto;
`;

const Popup = styled.div`
  display: block;
  width: 568px;
  line-height: 1.2;

  @media (max-width: 829px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 100%;
  }
`;

const Header = styled.div`
  position: relative;
  width: 100%;
  font-size: 12px;
  line-height: 19px;
  padding: 10px;
  padding-left: 22px;
  padding-right: 52px;
  color: ${({ theme }) => theme.operationChequeColor};
  background-color: ${({ theme }) => theme.popupDarkBackground};

  @media (max-width: 829px) {
    padding: 15px;
    padding-left: 15px;
    padding-right: 42px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  @media (max-width: 479px) {
    flex-direction: column;
  }
`;

const StockContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: baseline;
  font-weight: 700;

  @media (min-width: 480px) {
    ${({ theme }) => {
      if (theme.direction === "rtl") {
        return css`
          margin-left: auto;
        `;
      }
      return css`
        margin-right: auto;
      `;
    }};
  }
`;

const StockItem = styled.div`
  @media (min-width: 480px) {
    ${({ theme }) => {
      if (theme.direction === "rtl") {
        return css`
          margin-left: 10px;
        `;
      }
      return css`
        margin-right: 10px;
      `;
    }};
  }
`;

const Stock = styled(StockItem)`
  font-size: 16px;
  color: ${({ theme }) => theme.foreground};
  text-transform: uppercase;

  @media (min-width: 480px) {
    ${({ theme }) => {
      if (theme.direction === "rtl") {
        return css`
          margin-left: 10px;
        `;
      }
      return css`
        margin-right: 10px;
      `;
    }};
  }
`;

const Percent = styled(StockItem)`
  @media (max-width: 479px) {
    margin: 0 10px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 21px;
  height: 22px;
  margin-left: auto;
  color: #99959d;
  cursor: pointer;
  transition: all 0.3s ease;

  :hover {
    color: ${({ theme }) => theme.foreground};
  }
`;

const MainContainer = styled.div`
  @media (max-width: 829px) {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    background-color: ${({ theme }) => theme.main};
  }
`;

const ChartContainer = styled.div`
  position: relative;
  display: flex;
  align-content: center;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.main};
  ${"" /* border-top: 1px solid ${({ theme }) => theme.ticksBorderColor};
  border-bottom: 1px solid ${({ theme }) => theme.ticksBorderColor}; */} @media (min-width: 830px) {
    height: 245px;
  }

  @media (max-width: 829px) {
    flex-grow: 1;
    flex-shrink: 1;
    overflow: hidden;
  }
`;

const Info = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  flex-grow: 0;
  flex-shrink: 0;
  padding: 10px 22px;
  background-color: ${({ theme }) => theme.popupDarkBackground};
`;

const Content = styled.div`
  padding: 12px 0;
  font-size: 16px;
  line-height: 19px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};

  @media (max-width: 479px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 5px;
  }
`;

const Caption = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.operationChequeColor};
`;

const WinningsWrapper = styled.span`
  ${({ theme, winner }) => {
    switch (winner) {
      case false:
        return css`
          color: ${theme.lower};
        `;
      case true:
        return css`
          color: ${theme.upper};
        `;
      case null:
      default:
        return css`
          color: ${theme.ticksFontColor};
        `;
    }
  }};
`;

const ValueContainer = styled.div`
  position: absolute;
  top: 1px;
  left: 0;
  z-index: ${Layers.TOOLTIP};
`;

const Pointer = styled.div`
  position: absolute;
  left: -4px;
  top: 0;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-top: none;

  ${({ theme, positive }) => {
    switch (positive) {
      case false:
        return css`
          border-bottom-color: ${theme.lower};
        `;
      case true:
      case null:
      default:
        return css`
          border-bottom-color: ${theme.upper};
        `;
    }
  }};
`;

const ValueView = styled.div`
  position: relative;
  top: 6px;
  left: -50%;
  padding: 5px 10px;
  font-weight: bold;
  font-size: 14px;
  color: #fff;
  border-radius: 4px;

  ${({ theme, positive }) => {
    switch (positive) {
      case false:
        return css`
          background-color: ${theme.lower};
          border-bottom-color: ${theme.lower};
        `;
      case true:
      case null:
      default:
        return css`
          background-color: ${theme.upper};
          border-bottom-color: ${theme.upper};
        `;
    }
  }};

  @media (max-width: 959px) {
    ${({ alignment }) => {
      switch (alignment) {
        case "right":
          return css`
            left: -80%;
          `;
        case "left":
        default:
          return css`
            left: -20%;
          `;
      }
    }};
  }
`;

type ValueProps = {
  alignment?: "right",
  style: { [key: string]: string | null },
  positive?: boolean,
  children: React.Node
};

const Value = ({ x, y, positive, alignment, children }: ValueProps) => {
  const style = {
    WebkitTransform: `translateX(${x - 1}px) translateY(${y}px)`,
    transform: `translateX(${x - 1}px) translateY(${y}px)`
  };
  return (
    <ValueContainer style={style}>
      <ValueView positive={positive} alignment={alignment}>
        {children}
      </ValueView>
      <Pointer positive={positive} />
    </ValueContainer>
  );
};

const Cell = ({ caption, children }) => (
  <Content>
    <Caption>{caption}:</Caption>
    <Media
      query="(min-width: 480px)"
      render={() => <React.Fragment>&nbsp;</React.Fragment>}
    />
    <span>{children}</span>
  </Content>
);

let SimplifiedOperationChartVisuals = props => (
  <ThemeConsumer>
    {theme => {
      let lineColor =
        props.settings.lineColor === theme.background
          ? theme.foreground
          : props.settings.lineColor;

      let operationView = null;

      if (props.deals != null && props.deals.length > 0) {
        let operation = props.deals[0];

        const start = props.scaleX(operation.open_timestamp * 1000);
        const end = props.scaleX(operation.close_timestamp * 1000);
        let open = props.scaleY(operation.value);
        let close = props.scaleY(operation.close_value);

        let profitable = null;

        if (operation.close_value !== operation.value) {
          profitable = operation.bidType
            ? operation.close_value > operation.value
            : operation.close_value < operation.value;
        }

        operationView = (
          <React.Fragment>
            <Deal
              mobile={true}
              tooltip={false}
              {...operation}
              scaleX={props.scaleX}
              scaleY={props.scaleY}
              viewportWidth={props.width}
              lastPoint={props.lastPoint}
              tradeMode={props.tradeMode}
            />
            <LeftIndicatorView
              style={{
                WebkitTransform: `translate3d(${end}px, ${close}px, 0)`,
                transform: `translate3d(${end}px, ${close}px, 0)`,
                backgroundColor: profitable
                  ? "rgb(96,163,28)"
                  : "rgb(230,76,60)"
              }}
              mobile={true}
            />
            <Value
              x={start}
              y={open}
              positive={operation.bidType}
              alignment="left"
            >
              {operation.value && operation.value.toFixed(6)}
            </Value>
            <Value x={end} y={close} positive={profitable} alignment="right">
              {operation.close_value && operation.close_value.toFixed(6)}
            </Value>
          </React.Fragment>
        );
      }

      return (
        <ChartContainer
          {...props.containerProps}
          ref={props.containerProps.ref}
          // ref={undefined}
        >
          <Surface width={props.width} height={props.height}>
            <Line
              data={props.points}
              width={props.width}
              height={props.height}
              scaleX={props.scaleX}
              scaleY={props.scaleY}
              color={lineColor}
              strokeWidth={2}
              displayGradient={true}
            />
          </Surface>
          <HorizontalTicks
            mobile={true}
            scaleX={props.scaleX}
            ticks={props.scaleX.ticks(4)}
          />
          <VerticalTicks
            mobile={true}
            scaleY={props.scaleY}
            ticks={props.scaleY.ticks(2)}
          />
          {operationView}
        </ChartContainer>
      );
    }}
  </ThemeConsumer>
);

let mapStockHistoryToPoints = memoize(history => {
  if (history == null) {
    return [];
  }

  const dates = Object.keys(history);
  let points = [];

  for (let date of dates) {
    // HACK: Чиним формат дат чтобы они корректно парсились в сафари
    const correctDate = new Date(`${date.replace(" ", "T")}+00:00`).valueOf();

    points.push({
      date: correctDate,
      value: Number(history[date])
    });
  }

  return points;
});

// Скопировано из Chart/SectionData и изменено под наши нужды
let fetchSection = (operation, type) => async (section, profile) => {
  try {
    const interval = section.interval / 1000;
    const symbol = section.stock;

    const [start, end] = Helpers.getSectionBoundaries(section).map(
      b => b / 1000
    );

    let operation_type = type === "tournament" ? "tournament" : "binary";

    const response = await apiCall(
      `/api/stocks/history/csv?symbol=${symbol}&interval=${interval}&start=${start}&end=${end}&user_id=${
        profile.id
      }&operation_id=${operation.id}&operation_type=${operation_type}`
    );

    if (response.status !== 200) {
      switch (response.status) {
        case 400:
          return {
            section,
            data: []
          };
        case 403:
          return {
            section,
            data: "darkness"
          };
        default:
          throw new TypeError(
            `Failed to load the history for ${symbol} from ${new Date(
              start * 1000
            ).toString()} to ${new Date(
              end * 1000
            ).toString()} due to HTTP status code mismatch (${response.status})`
          );
      }
    }

    const text = await response.text();
    const lines = text.split("\n");
    const data = [];

    for (const line of lines) {
      if (line.length > 0) {
        const [epoch, open, close, min, max] = line.split(",");

        data.push({
          epoch: parseInt(epoch, 10),
          open: parseFloat(open),
          close: parseFloat(close),
          min: parseFloat(min),
          max: parseFloat(max)
        });
      }
    }

    if (data.length === 0) {
      return {
        section,
        data: "void"
      };
    }

    return {
      section,
      data
    };
  } catch (error) {
    console.log(error);
    return {
      section,
      data: []
    };
  }
};

const fetchData = ({ url, errorHandler, ...options }) =>
  window
    .fetch(url, options)
    .then(response => response.json())
    .catch(errorHandler);

class OperationChart extends React.Component<$FlowFixMeProps, $FlowFixMeState> {
  state = {
    isFetching: true,
    stockHistory: null,
    isError: false
  };

  componentDidMount() {
    //TODO remake with new api
    const props = this.props;
    const host = window.API_URL || (process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && "https://testapp.irontrade.com");

    fetchData({
      url: `${host}/ajax/stockHistory?stock=${
        props.stock.stock
      }&limit=${7200}&tz=UTC`,
      errorHandler: () => {
        this.setState({
          isError: true
        });
      }
    }).then(stockHistory => this.setState({ stockHistory, isFetching: false }));
  }

  render() {
    const props = this.props;
    const { stockHistory, isFetching, isError } = this.state;
    const points = mapStockHistoryToPoints(stockHistory);

    if (isFetching) {
      return (
        <ChartContainer>
          <Spinner accent />
        </ChartContainer>
      );
    }

    if (isError) {
      return <ChartContainer>Sorry, error occured</ChartContainer>;
    }

    if (points.length < 5) {
      return <ChartContainer>No data for view</ChartContainer>;
    }

    return (
      <Chart
        static
        key={props.stock.stock}
        stock={props.stock}
        tradeMode="binary"
        points={points}
        settings={{
          indicators: [],
          timeSpan: (props.operation.bTime * 1000) / 0.8,
          interval: 1,
          chartType: "line",
          lineColor: props.lineColor
        }}
        cursor={{
          date: props.operation.open_timestamp * 1000,
          ratio: 0.1,
          follow: false,
          animate: false
        }}
        stats={null}
        profile={props.profile}
        deals={[props.operation]}
        intent={null}
        timeUntilNextLockToggle={null}
        dispatch={props.dispatch}
        onEditIndicator={() => void 0}
        interactive={false}
        visuals={SimplifiedOperationChartVisuals}
        fetchSection={fetchSection(props.operation, props.type)}
        allowShortHistorySections={true}
      />
    );
  }
}

let UpperColoredText = styled.span`
  color: ${({ theme }) => theme.upper};
`;

let LowerColoredText = styled.span`
  color: ${({ theme }) => theme.lower};
`;

let BidType = props => {
  if (props.operation.bidType === true) {
    return (
      <UpperColoredText>
        <UpIcon />
        &nbsp;
        <Gettext>Upper</Gettext>
      </UpperColoredText>
    );
  } else {
    return (
      <LowerColoredText>
        <DownIcon />
        &nbsp;
        <Gettext>lower</Gettext>
      </LowerColoredText>
    );
  }
};

type ChequeProps = {
  operation: Operation,
  style: string,
  onDismiss: () => void,
  tradeMode: "binary" | "tournament",
  asset: Asset,
  profile: Profile
};

function findAsset(assets, stock) {
  if (stock === "CRYPTO") {
    stock = "CRYPTOINDEX";
  }

  for (let category of Object.keys(assets)) {
    for (let asset of assets[category]) {
      if (asset.stock === stock) {
        return asset;
      }
    }
  }

  throw new TypeError("Unknown asset " + stock);
}

let Cheque = (props: ChequeProps) => {
  const { operation, onDismiss, style, api } = props;

  const start = operation.open_timestamp * 1000;
  const end = operation.close_timestamp * 1000;

  const currency = props.tradeMode === "tournament" ? tournamentCurrency : null;
  let winner = Math.sign(parseFloat(operation.profit)) >= 0;

  if (operation.close_value === operation.value) {
    winner = null;
  }

  let profile = api.readEntity("/api/profile").value;
  let assets = api.readEntity("/api/getAssets").value;

  if (profile == null || assets == null) {
    return null;
  }

  let asset = findAsset(assets, operation.stock);
  const times = React.useMemo(() => {
    return (
      <span>
        {moment(start).format("ll HH:mm:ss")}
        &nbsp;–&nbsp;
        {moment(end).format("ll HH:mm:ss")}
      </span>
    );
  }, [start, end]);

  const MemoHeader = React.useMemo(() => {
    return (
      <Header>
        <HeaderContainer>
          <StockContainer>
            <Stock>{operation.stock}</Stock>
            <Percent>
              <Gettext>PROFIT</Gettext>: {operation.percent}%
            </Percent>
          </StockContainer>
          {times}
          <CloseButton>
            <Close onClick={onDismiss} />
          </CloseButton>
        </HeaderContainer>
        <span>
          <Gettext>ID</Gettext> {operation.id}
        </span>
      </Header>
    );
  }, [operation, times, onDismiss]);

  const MemoOperationChart = React.useMemo(() => {
    return (
      <OperationChart
        api={api}
        stock={asset}
        profile={profile}
        operation={operation}
        type={props.type}
        lineColor={props.lineColor}
      />
    );
  }, [api, asset, profile, operation, props.type, props.lineColor]);

  return (
    <Overlay style={style}>
      <ClickOutside onOutsideClick={onDismiss}>
        {({ innerRef }) => (
          <Popup ref={innerRef}>
            {MemoHeader}
            <MainContainer>
              {MemoOperationChart}
              <Info>
                <Cell caption={<Gettext>Result</Gettext>}>
                  <WinningsWrapper winner={winner}>
                    {operation.profit === 0 ? (
                      <Refund />
                    ) : (
                      <React.Fragment>
                        {winner && "+"}
                        <Money currency={currency}>{operation.profit}</Money>
                      </React.Fragment>
                    )}
                  </WinningsWrapper>
                </Cell>
                <Cell caption={<Gettext>Amount</Gettext>}>
                  <Money currency={currency}>{operation.amount}</Money>
                </Cell>
                <Cell caption={<Gettext>Type</Gettext>}>
                  <BidType operation={operation} />
                </Cell>
              </Info>
            </MainContainer>
          </Popup>
        )}
      </ClickOutside>
    </Overlay>
  );
};

let ApiedCheque = props => {
  const api = React.useContext(Api);
  const theme = React.useContext(TradeThemeContext);

  return (
    <ThemeProvider theme={theme}>
      <Cheque {...props} api={api} />
    </ThemeProvider>
  );
};

export default connect(state => ({
  tradeMode: state.settings.mode,
  lineColor: state.settings.lineColor
}))(ApiedCheque);



// WEBPACK FOOTER //
// ./src/components/Trade/OperationCheque.js